import { Fragment, useMemo } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ActorType } from '@/types/commission-policy.types';
import { CommissionPolicyForm } from '@/pages/commission-policy/components/CommissionPolicyForm';
import { useGetCommissionPolicyByActorQuery } from '@/services/CommissionPolicyService';
import { usePermissions } from '@/hooks/usePermissions';

const ServiceProviderCommissionPolicyPage = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceProviderId } = useParams<{ serviceProviderId: string }>();
  const [searchParams] = useSearchParams();

  const { hasPermission } = usePermissions();
  const canRead = hasPermission('commission_policy_management_read');
  const canCreate = hasPermission('commission_policy_management_create');
  const canUpdate = hasPermission('commission_policy_management_update');

  const modeParam = searchParams.get('mode');
  const mode = (
    modeParam === 'edit' || modeParam === 'create' || modeParam === 'view'
      ? modeParam
      : 'view'
  ) as 'view' | 'edit' | 'create';

  const { data: policy, isLoading } = useGetCommissionPolicyByActorQuery(
    {
      actor_id: serviceProviderId || null,
      actor_type: ActorType.SERVICE_PROVIDER,
    },
    { skip: !serviceProviderId }
  );

  const displayedPolicy = policy ?? null;
  const actorSpecificPolicy = useMemo(() => {
    if (!policy) return null;
    return policy.actor_id === null ? null : policy;
  }, [policy]);

  const effectiveMode = useMemo(() => {
    if (!actorSpecificPolicy && mode === 'edit') return 'view';
    if (mode === 'create' && !canCreate) return 'view';
    if (mode === 'edit' && !canUpdate) return 'view';
    return mode;
  }, [actorSpecificPolicy, canCreate, canUpdate, mode]);

  const readOnlyReason = useMemo(() => {
    if (isLoading) return 'Loading commission policy...';
    if (!actorSpecificPolicy && displayedPolicy && effectiveMode === 'view') {
      return 'Using global commission policy.';
    }
    if (!canRead) {
      return "You don't have permission to view commission policies.";
    }
    if (effectiveMode === 'create' && !canCreate) {
      return "You don't have permission to create commission policies.";
    }
    if (effectiveMode === 'edit' && !canUpdate) {
      return "You don't have permission to update commission policies.";
    }
    return undefined;
  }, [
    actorSpecificPolicy,
    canCreate,
    canRead,
    canUpdate,
    displayedPolicy,
    effectiveMode,
    isLoading,
  ]);

  const handleDone = () => {
    navigate(`/service-providers/${serviceProviderId}/commission`, {
      replace: true,
    });
  };

  const handleSet = () => {
    navigate(`/service-providers/${serviceProviderId}/commission?mode=create`, {
      replace: true,
    });
  };

  const handleEdit = () => {
    navigate(`/service-providers/${serviceProviderId}/commission?mode=edit`, {
      replace: true,
    });
  };

  const handleBack = () => {
    const from = (location.state as { from?: string } | null)?.from;
    if (from) {
      navigate(from);
      return;
    }
    navigate(-1);
  };

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <div className="text-base font-semibold">Commission Policy</div>
              <ToolbarDescription>
                Service provider commercial terms: commission policy.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={handleBack}
              >
                Back
              </button>
              {canUpdate && actorSpecificPolicy && effectiveMode === 'view' && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleEdit}
                >
                  Edit
                </button>
              )}
              {canCreate &&
                !actorSpecificPolicy &&
                effectiveMode === 'view' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={handleSet}
                  >
                    Set commission policy
                  </button>
                )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <Container>
        <CommissionPolicyForm
          mode={
            actorSpecificPolicy
              ? effectiveMode
              : effectiveMode === 'create'
                ? 'create'
                : 'view'
          }
          policy={displayedPolicy}
          actorPayload={{
            actor_type: ActorType.SERVICE_PROVIDER,
            actor_id: serviceProviderId || null,
          }}
          onDone={handleDone}
          readOnlyReason={readOnlyReason}
        />
      </Container>
    </Fragment>
  );
};

export default ServiceProviderCommissionPolicyPage;
